import React, { useState, useRef, useEffect } from 'react'
import { TimeEntry, WeeklyEntries, ViewMode } from '../../types'
import { timeUtils } from '../../services/timerService'
import { 
  Clock, Square, Edit, Trash2, PlusCircle, 
  MoreVertical, StopCircle, Calendar, TrendingUp 
} from 'lucide-react'
import ViewToggle from './ViewToggle'
import WeeklyTimeEntryList from './WeeklyTimeEntryList'

interface TimeEntryListProps {
  entries: TimeEntry[]
  totalDuration: number
  weeklyEntries?: WeeklyEntries
  weeklyTotalDuration?: number
  isWeeklyLoaded?: boolean
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  onEdit?: (entry: TimeEntry) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  onManualAdd?: () => void
  className?: string
  disabled?: boolean
}

const TimeEntryList: React.FC<TimeEntryListProps> = ({
  entries,
  totalDuration,
  weeklyEntries,
  weeklyTotalDuration,
  isWeeklyLoaded = false,
  viewMode,
  onViewModeChange,
  onEdit,
  onDelete,
  onStop,
  onManualAdd,
  className = '',
  disabled = false
}) => {
  const currentTotalDuration = viewMode === 'today' ? totalDuration : (weeklyTotalDuration || 0)
  const totalLabel = viewMode === 'today' ? '今日' : '本週'

  return (
    <div className={`${className}`}>
      {/* 標題區域 - 響應式設計 */}
      <div className="time-entry-header mb-6">
        {/* 桌面版和平板版布局 (md+, ≥768px) - 單行簡潔版 */}
        <div className="hidden md:flex items-center justify-between gap-2">
          {/* 左側：標題 */}
          <div className="flex items-center gap-2 3xl:gap-3 flex-shrink min-w-0">
            <div className="time-entry-header-icon flex-shrink-0">
              <Calendar className="w-5 h-5" />
            </div>
            <div className="hidden 3xl:block flex-shrink-0">
              <h3 className="text-lg font-bold text-secondary-800 leading-tight whitespace-nowrap">
                時間記錄
              </h3>
              <p className="text-xs text-secondary-400 mt-0.5 whitespace-nowrap">
                追蹤您的工作時間
              </p>
            </div>
          </div>
          
          {/* 右側：操作按鈕 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <ViewToggle 
              currentView={viewMode}
              onViewChange={onViewModeChange}
            />
            
            {onManualAdd && (
              <button
                onClick={disabled ? undefined : onManualAdd}
                disabled={disabled}
                className="time-entry-add-btn group flex-shrink-0"
              >
                <span className="time-entry-add-btn-icon">
                  <PlusCircle className="w-4 h-4" />
                </span>
                <span className="hidden 3xl:inline">手動新增</span>
              </button>
            )}
          </div>
        </div>

        {/* 手機版布局 (< md, <768px) - 兩行緊湊版 */}
        <div className="md:hidden space-y-4">
          {/* 第一行：標題 + 新增按鈕 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="time-entry-header-icon">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-secondary-800 leading-tight">
                  時間記錄
                </h3>
                <p className="text-[11px] text-secondary-400 mt-0.5">
                  追蹤您的工作時間
                </p>
              </div>
            </div>
            
            {onManualAdd && (
              <button
                onClick={disabled ? undefined : onManualAdd}
                disabled={disabled}
                className="time-entry-add-btn group"
              >
                <span className="time-entry-add-btn-icon">
                  <PlusCircle className="w-4 h-4" />
                </span>
              </button>
            )}
          </div>

          {/* 第二行：統計 + 檢視切換 */}
          <div className="flex items-center justify-between gap-3">
            {/* 總計徽章 - 緊湊版 (手機版保留，因為看不到 Header) */}
            <div className="time-entry-stats-badge flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="time-entry-stats-icon flex-shrink-0">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-[10px] uppercase tracking-wider text-secondary-400 font-medium whitespace-nowrap">
                    {totalLabel}
                  </span>
                  <span className="text-sm font-bold text-secondary-800 font-mono truncate">
                    {timeUtils.formatDurationHuman(currentTotalDuration)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* 檢視切換 */}
            <div className="flex-shrink-0">
              <ViewToggle 
                currentView={viewMode}
                onViewChange={onViewModeChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 記錄列表 */}
      <div className="space-y-3">
        {viewMode === 'today' ? (
          // 今日檢視
          entries.length === 0 ? (
            <EmptyState />
          ) : (
            entries.map((entry, index) => (
              <TimeEntryItem
                key={entry.id}
                entry={entry}
                onEdit={onEdit}
                onDelete={onDelete}
                onStop={onStop}
                disabled={disabled}
                index={index}
              />
            ))
          )
        ) : (
          // 週檢視 — 用 isWeeklyLoaded 區分「尚未載入」與「真的沒紀錄」
          // 初始 weeklyEntries 是 {}（truthy），無法用它本身判斷載入狀態
          !isWeeklyLoaded ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-secondary-500">
                <div className="w-5 h-5 border-2 border-secondary-300 border-t-primary-500
                  rounded-full animate-spin" />
                <span>載入中...</span>
              </div>
            </div>
          ) : (
            <WeeklyTimeEntryList
              weeklyEntries={weeklyEntries || {}}
              onEdit={onEdit}
              onDelete={onDelete}
              onStop={onStop}
              disabled={disabled}
            />
          )
        )}
      </div>
    </div>
  )
}

// 空狀態組件
const EmptyState: React.FC = () => (
  <div className="empty-state">
    <div className="empty-state-icon">
      <Clock className="w-10 h-10" />
    </div>
    <h4 className="empty-state-title">今日尚無記錄</h4>
    <p className="empty-state-desc">
      選擇專案並點擊開始計時，建立第一筆工時記錄
    </p>
  </div>
)

// 更多操作選單組件
interface MoreActionsDropdownProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  disabled?: boolean
}

const MoreActionsDropdown: React.FC<MoreActionsDropdownProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isRunning = entry.status === 'RUNNING'

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleAction = (action: () => void) => {
    action()
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="p-2 rounded-lg transition-all duration-200
          text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100
          disabled:text-secondary-300 disabled:cursor-not-allowed"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && !disabled && (
        <div className="absolute right-0 top-10 w-36 glass-card rounded-xl 
          shadow-lg border border-secondary-200/50 py-1.5 z-20 animate-fade-in">
          {isRunning ? (
            onStop && (
              <button
                onClick={() => handleAction(onStop)}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm 
                  text-red-600 hover:bg-red-50 transition-colors"
              >
                <StopCircle className="w-4 h-4" />
                <span>停止計時</span>
              </button>
            )
          ) : (
            <>
              {onEdit && (
                <button
                  onClick={() => handleAction(() => onEdit(entry))}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm 
                    text-secondary-700 hover:bg-secondary-50 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  <span>編輯記錄</span>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => handleAction(() => onDelete(entry.id))}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm 
                    text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>刪除記錄</span>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// 單筆計時記錄組件
interface TimeEntryItemProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  disabled?: boolean
  index?: number
}

const TimeEntryItem: React.FC<TimeEntryItemProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  disabled = false,
  index = 0
}) => {
  const isRunning = entry.status === 'RUNNING'
  const duration = isRunning 
    ? timeUtils.calculateDuration(entry.startTime)
    : entry.duration

  return (
    <div 
      className={`
        time-entry-card group
        ${isRunning ? 'time-entry-card-running' : ''}
        animate-fade-in
      `}
      style={{ 
        animationDelay: `${index * 50}ms`,
        '--entry-accent-color': entry.project.color 
      } as React.CSSProperties}
    >
      {/* 內容區塊 - 左側留空給色帶 */}
      <div className="flex items-stretch min-w-0">
        {/* 主要內容 */}
        <div className="flex-1 min-w-0 p-4 md:p-5 pl-6 md:pl-7">
          {/* 上方：專案資訊 + 狀態標籤 */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              {/* 專案名稱 + 顏色點 */}
              <div className="flex items-center gap-2.5 mb-2">
                <div 
                  className="time-entry-project-dot w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: entry.project.color }}
                />
                <h4 className="text-base font-semibold text-secondary-800 truncate">
                  {entry.project.name}
                </h4>
              </div>
              
              {/* 描述 */}
              {entry.description ? (
                <p className="text-sm text-secondary-500 line-clamp-2 leading-relaxed">
                  {entry.description}
                </p>
              ) : (
                <p className="text-sm text-secondary-400 italic">
                  無描述
                </p>
              )}
              {/* 手動新增原因 */}
              {entry.isManual && entry.manualReason && (
                <p className="text-xs text-amber-600 mt-1">
                  手動新增原因：{entry.manualReason}
                </p>
              )}
            </div>

            {/* 右側：時長顯示 */}
            <div className={`
              time-entry-duration flex-shrink-0
              ${isRunning ? 'time-entry-duration-running' : 'text-secondary-700'}
            `}>
              {timeUtils.formatDuration(duration)}
            </div>
          </div>

          {/* 下方：狀態標籤 + 時間資訊 + 操作按鈕 */}
          <div className="flex items-center justify-between gap-2 min-w-0">
            {/* 左側：標籤和時間 */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
              {/* 狀態標籤 */}
              {isRunning ? (
                <span className="time-entry-status time-entry-status-running">
                  <div className="status-indicator status-indicator-running">
                    <div className="status-indicator-dot" />
                  </div>
                  進行中
                </span>
              ) : (
                <span className="time-entry-status time-entry-status-completed">
                  <Square className="w-3 h-3" />
                  已完成
                </span>
              )}
              
              {/* 手動/編輯標籤 */}
              {entry.isManual && (
                <span
                  className="time-entry-status time-entry-status-manual"
                  title={entry.manualReason ? `原因：${entry.manualReason}` : '手動新增'}
                >
                  <PlusCircle className="w-3 h-3" />
                  手動
                </span>
              )}
              {entry.isEdited && (
                <span className="time-entry-status time-entry-status-edited">
                  <Edit className="w-3 h-3" />
                  已編輯
                </span>
              )}

              {/* 分隔線 */}
              <div className="hidden sm:block w-px h-4 bg-secondary-200" />

              {/* 時間資訊 */}
              <div className="time-entry-time-info">
                <span className="time-entry-time-badge">
                  <Clock className="w-3 h-3" />
                  {timeUtils.formatTime(entry.startTime)}
                </span>
                {entry.endTime ? (
                  <>
                    <span className="time-entry-time-arrow">→</span>
                    <span className="time-entry-time-badge">
                      {timeUtils.formatTime(entry.endTime)}
                    </span>
                  </>
                ) : (
                  <span className="text-green-600 text-xs font-medium animate-pulse">
                    計時中...
                  </span>
                )}
              </div>
            </div>

            {/* 右側：操作按鈕 */}
            <div className="flex items-center">
              {/* 桌機版：hover 顯示按鈕 */}
              {!isRunning && (
                <div className="time-entry-actions hidden lg:flex opacity-0 group-hover:opacity-100 
                  transition-all duration-200 transform translate-x-2 group-hover:translate-x-0">
                  {onEdit && (
                    <button
                      onClick={disabled ? undefined : () => onEdit(entry)}
                      disabled={disabled}
                      className="time-entry-action-btn
                        disabled:opacity-50 disabled:cursor-not-allowed"
                      title="編輯記錄"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={disabled ? undefined : () => onDelete(entry.id)}
                      disabled={disabled}
                      className="time-entry-action-btn time-entry-action-btn-danger
                        disabled:opacity-50 disabled:cursor-not-allowed"
                      title="刪除記錄"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              
              {/* 更多操作選單 - 桌機 hover 時隱藏 */}
              <div className={`${!isRunning ? 'lg:opacity-100 lg:group-hover:opacity-0 lg:group-hover:pointer-events-none' : ''} transition-opacity duration-200`}>
                <MoreActionsDropdown
                  entry={entry}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onStop={onStop}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimeEntryList
